import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FormDialog } from '@/components/ui/form-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RecordLink } from '@/components/records/RecordLink';
import api from '@/lib/api';
import { formatCurrency, humanizeEnum } from '@/lib/utils';

export type ReversalReasonCode =
  | 'STOCK_TOO_LOW'
  | 'LOCATION_INACTIVE'
  | 'TRACKED_PRODUCT'
  | 'IS_REVERSAL'
  | 'NOT_ADJUSTMENT'
  | 'ZERO_APPLIED'
  | 'MODE_MISMATCH';

/** The block the API puts on every movement row (POS-I244). The page renders it, never infers it. */
export interface MovementReversal {
  state: 'AVAILABLE' | 'REVERSED' | 'BLOCKED' | 'NOT_REVERSIBLE';
  reasonCode: ReversalReasonCode | null;
  qty: number;
  value: number;
  journalEntry: { id: string; number: string; status: string } | null;
  stockAfter: number | null;
  onHand: number | null;
  reversedBy: { id: string; createdAt: string } | null;
  reverses: { id: string } | null;
}

export interface ReversibleMovement {
  id: string;
  createdAt: string;
  type?: string;
  quantity?: number;
  reason?: string | null;
  product?: { name?: string } | null;
  reversal?: MovementReversal | null;
}

const REASON_MIN = 5;
const REASON_MAX = 500;

/** Query keys (first segment) that show stock or ledger figures a reversal changes. */
const STALE_PREFIXES = ['inventory', 'stock', 'products', 'low-stock', 'journal', 'tenant-dashboard', 'record-viewer', 'trial-balance', 'profit', 'balance-sheet'];

function inlineError(err: unknown, t: (k: string, o?: Record<string, unknown>) => string): { text: string; alreadyReversed: boolean } {
  const res = (err as { response?: { status?: number; data?: { code?: string; message?: string; onHand?: number; applied?: number } } })?.response;
  const code = res?.data?.code;
  if (res?.status === 409 || code === 'ALREADY_REVERSED') return { text: t('reverseErrAlready'), alreadyReversed: true };
  if (code === 'STOCK_TOO_LOW') return { text: t('reverseBlockedTooLow', { onHand: res?.data?.onHand ?? 0, qty: res?.data?.applied ?? 0 }), alreadyReversed: false };
  if (code === 'TRACKED_PRODUCT') return { text: t('reverseBlockedTracked'), alreadyReversed: false };
  if (code === 'LOCATION_INACTIVE') return { text: t('reverseBlockedLocation'), alreadyReversed: false };
  if (res?.status === 403) return { text: t('reverseErrForbidden'), alreadyReversed: false };
  return { text: res?.data?.message || t('reverseErrGeneric'), alreadyReversed: false };
}

/**
 * POS-I244. Reverse adjustment: states the product, the exact stock change and the accounting
 * effect (the entry is a link that opens in place), asks for a required reason and confirms.
 */
export function ReverseAdjustmentDialog({ movement, onClose }: { movement: ReversibleMovement | null; onClose: () => void }) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setError(null);
  }, [movement?.id]);

  const r = movement?.reversal ?? null;
  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/inventory/movements/${movement!.id}/reverse`, { reason: trimmed });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('reverseDone'));
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && STALE_PREFIXES.some((p) => (q.queryKey[0] as string).startsWith(p)),
      });
      onClose();
    },
    onError: (err: unknown) => {
      const e = inlineError(err, t as never);
      setError(e.text);
      // already reversed elsewhere: the list is stale, refresh it so the row shows the badge
      if (e.alreadyReversed) queryClient.invalidateQueries({ queryKey: ['inventory-movements-full'] });
    },
  });

  if (!movement || !r) return null;

  const delta = r.stockAfter != null && r.onHand != null ? r.stockAfter - r.onHand : 0;
  const deltaText = `${delta > 0 ? '+' : ''}${delta}`;
  const je = r.journalEntry;

  return (
    <FormDialog
      open
      onClose={onClose}
      title={t('reverseTitle')}
      subtitle={movement.product?.name ?? undefined}
      variant="destructive"
      submitLabel={t('reverseAction')}
      isPending={mutation.isPending}
      submitDisabled={!reasonOk}
      onSubmit={(e) => {
        e.preventDefault();
        if (!reasonOk) return;
        setError(null);
        mutation.mutate();
      }}
    >
      <div className="space-y-4 text-sm">
        <dl className="space-y-1.5">
          <div className="flex gap-2">
            <dt className="w-32 shrink-0 text-muted-foreground">{t('reverseOriginal')}</dt>
            <dd>
              <bdi dir="ltr">{new Date(movement.createdAt).toLocaleString()}</bdi>
              {' · '}
              {humanizeEnum(movement.type)} <bdi dir="ltr" className="tabular-nums">x{r.qty}</bdi>
            </dd>
          </div>
          {movement.reason && (
            <div className="flex gap-2">
              <dt className="w-32 shrink-0 text-muted-foreground">{t('reverseOriginalReason')}</dt>
              <dd className="min-w-0 break-words">{movement.reason}</dd>
            </div>
          )}
          <div className="flex gap-2">
            <dt className="w-32 shrink-0 text-muted-foreground">{t('reverseStock')}</dt>
            <dd data-testid="reverse-stock-change">
              <bdi dir="ltr" className="tabular-nums">
                {t('reverseStockChange', { from: r.onHand ?? '', to: r.stockAfter ?? '', delta: deltaText })}
              </bdi>
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-32 shrink-0 text-muted-foreground">{t('reverseAccounting')}</dt>
            <dd data-testid="reverse-accounting" className="min-w-0">
              {je && je.status === 'POSTED' && (
                <span>
                  {t('reverseAcctPostedLead')}{' '}
                  <RecordLink type="journalEntry" id={je.id} label={je.number} variant="mono" />
                  {' '}
                  <bdi dir="ltr" className="tabular-nums">{formatCurrency(r.value)}</bdi>
                  {' '}{t('reverseAcctPostedTail')}
                </span>
              )}
              {je && je.status !== 'POSTED' && (
                <span>
                  <RecordLink type="journalEntry" id={je.id} label={je.number} variant="mono" />{' '}
                  {t('reverseAcctVoided')}
                </span>
              )}
              {!je && t('reverseAcctNone')}
            </dd>
          </div>
        </dl>

        <div className="space-y-1">
          <Label htmlFor="reverse-reason">{t('reverseReasonLabel')}</Label>
          <Textarea
            id="reverse-reason"
            rows={3}
            maxLength={REASON_MAX}
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(null); }}
            aria-invalid={!reasonOk && reason.length > 0 ? true : undefined}
            aria-describedby="reverse-reason-help"
            autoFocus
          />
          <div id="reverse-reason-help" className="flex items-start justify-between gap-2 text-xs">
            <span className={trimmed.length < REASON_MIN ? 'text-destructive-text' : 'text-muted-foreground'}>
              {trimmed.length < REASON_MIN ? t('reverseReasonMin') : ' '}
            </span>
            <span className="tabular-nums text-muted-foreground" dir="ltr">{reason.length}/{REASON_MAX}</span>
          </div>
        </div>

        {error && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-destructive-text" data-testid="reverse-error">
            {error}
          </p>
        )}
      </div>
    </FormDialog>
  );
}
